import React, { useContext, useRef, useCallback } from 'react'
import { useMediaQuery } from '@material-ui/core'
import DesktopMacOutlinedIcon from '@material-ui/icons/DesktopMacOutlined'
import { ProSidebar, Menu, MenuItem, SubMenu, SidebarContent, SidebarFooter } from 'react-pro-sidebar'
import { NavLink, useLocation } from 'react-router-dom'
import { useIntl } from 'react-intl'
import Tooltip from '@material-ui/core/Tooltip'
import AvatarDefault from '../../assets/img/avatar-default.png'

import {
  FaListAlt,
  FaCog,
  FaDollarSign,
  FaChartLine,
  FaStackOverflow,
  FaWarehouse,
  FaUserTag,
  FaPlus,
  FaListOl,
  FaBoxOpen,
  FaBoxes,
  FaUsersCog,
  FaTags,
  FaTag,
  FaCertificate,
  FaShapes,
  FaMapMarkedAlt,
  FaCogs,
  FaUsers,
  FaSlidersH,
  FaLayerGroup,
  FaCcMastercard,
  FaBarcode,
  FaBell,
  FaBullhorn,
  FaPen,
  FaImage,
  FaHistory,
  FaHeartbeat,
  FaUserShield,
  FaExchangeAlt,
  FaSms,
  FaRegCreditCard,
  FaChartPie,
  FaCalculator,
  FaClipboardList,
  FaMoneyBillWave,
  FaFileInvoiceDollar,
  FaCalendarCheck,
  FaTools,
  FaEnvelopeOpenText,
  FaUndo,
} from 'react-icons/fa'

import { IoIosClose } from 'react-icons/io'
import { FiPackage } from 'react-icons/fi'
import 'react-pro-sidebar/dist/css/styles.css'
import { AppContext } from '../../context/AppContext'
import { useAuthentication } from '../../customHooks/useAuthentication'
import { routes as SettingsRoutes } from '../../views/Settings/settings.route'
import { isCompanyAdmin } from '../../views/Account/account.utils'
import { showShopLegalNav } from '../../views/StorefrontLegal/legalPage.utils'
import paths from '../../routes/paths'
import usePlanModuleNav from '../../customHooks/usePlanModuleNav'
import { MODULE_ROUTE_CONFIG as pathnames, matchesModuleRoute, matchesModuleRouteConfig } from '../../routes/planModuleRouteMap'
import { buildAllowedSidebarModules, canShowUserManagementNav } from '../../utils/companyModules'
import { useAccess } from '../../access/AccessContext'
import { hasPermission, sidebarTokenAllowed } from '../../access/accessPolicy'

import imglogo from '../../assets/img/logo_t4i.png'
import '../../assets/css/App.css'

const stock_receipt = 'stock_receipt'
const stock_transfer = 'stock_transfer'
const adjustment = 'adjustment'
const sales = 'sales'
const customer = 'customer'
const expensive = 'expensive'
const products = 'products'
const userManagment = 'userManagment'
const warehouse = 'warehouse'
const vendor = 'vendor'
const configuration = 'configuration'
const pos = 'pos'
const reports = 'Reports'
const purchase_order = 'purchase_order'

const mapModule = {
  [userManagment]: 'User Management',
  [products]: 'Products',
  [warehouse]: 'WareHouse',
  [vendor]: 'Vendor',
  [stock_receipt]: 'Stock Receipt',
  [stock_transfer]: 'Stock Transfer',
  [adjustment]: 'Adjustment',
  [customer]: 'Costumers',
  [sales]: 'Sales',
  [expensive]: 'Expensive',
  [configuration]: 'Configuration',
  [reports]: 'Reports',
  [pos]: 'pos',
  [purchase_order]: 'Purchase Order',
}

const superAdminRoles = ['Super Admin']

const settingsNavRoutes = [...pathnames.settings, paths.settings.subscription]

const SidebarItem = ({ children, routesActivate = [], activePrefix = '', routeConfig, ...props }) => {
  const location = useLocation()
  const [open, setOpen] = React.useState(false)
  const config = routeConfig ?? routesActivate
  const routeMatches = matchesModuleRouteConfig(config, location.pathname) || (activePrefix && matchesModuleRoute(activePrefix, location.pathname))

  React.useEffect(() => {
    setOpen(routeMatches)
  }, [location, routeMatches])

  const isActive = routeMatches

  return (
    <SubMenu open={open} className={`${isActive ? 'active' : ''} ${props.className || ''}`.trim()} onClick={() => setOpen((prev) => !prev)} {...props}>
      {children}
    </SubMenu>
  )
}

const Aside = ({ rtl }) => {
  const { formatMessage: trans } = useIntl()
  const location = useLocation()
  const { userInfo } = useAuthentication()
  const roles = userInfo?.roles || []
  const isSuperAdmin = roles.some((rol) => superAdminRoles.includes(rol.name))
  const companyAdmin = isCompanyAdmin(userInfo)
  const { navigationExpanded, changeNavigationVisible } = useContext(AppContext)
  const proSidebarContainerRef = useRef()
  const isMobileNav = useMediaQuery('(max-width:768px)')
  const closeMobileSidebar = useCallback(() => {
    if (isMobileNav) changeNavigationVisible(true)
  }, [isMobileNav, changeNavigationVisible])
  const handleSidebarNavClick = useCallback(
    (event) => {
      if (event.target.closest('a[href]')) closeMobileSidebar()
    },
    [closeMobileSidebar],
  )

  React.useEffect(() => {
    closeMobileSidebar()
  }, [location.pathname, closeMobileSidebar])

  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !navigationExpanded) {
        changeNavigationVisible(true)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [navigationExpanded, changeNavigationVisible])

  const { access } = useAccess()
  const profileLocked = Boolean(access?.profileEnforced && access?.role !== 'admin' && !access?.isAdmin)
  const allowPermission = (permission) => !profileLocked || hasPermission(access, permission)
  const planModules = React.useMemo(() => buildAllowedSidebarModules(userInfo), [userInfo])
  const allowedModulesArray = React.useMemo(
    () => planModules.filter((token) => sidebarTokenAllowed(token, access || { ready: false })),
    [planModules, access],
  )
  const { canAccessPlanModule } = usePlanModuleNav(userInfo)

  return (
    <ProSidebar rtl={rtl} collapsed={navigationExpanded} ref={proSidebarContainerRef} role="navigation" aria-label="Main Sidebar Navigation">
      <div className="logo-header-container">
        <NavLink exact to={paths.dashboard} className="logo-header" aria-label="Tech4Inventory dashboard" onClick={handleSidebarNavClick}>
          <span className="logo-header__mark">
            <img src={imglogo} alt="" />
          </span>
          <span className="logo-header__text" style={{ display: !navigationExpanded ? undefined : 'none' }}>
            <span className="logo-header__brand">
              Tech<span className="logo-header__accent">4</span>
            </span>
            <span className="logo-header__product">Inventory</span>
          </span>
        </NavLink>
        <button className="close-icon" onClick={() => changeNavigationVisible(true)} aria-label="Close sidebar navigation" type="button">
          <IoIosClose color="white" style={{ width: '32px', height: '32px' }} />
        </button>
      </div>
      <SidebarContent onClick={handleSidebarNavClick}>
        <Menu iconShape="circle">
          <Tooltip title={trans({ id: 'dashboard', defaultMessage: 'Dashboard' })} placement="right" disableHoverListener={!navigationExpanded}>
            <MenuItem active={location.pathname === paths.dashboard} icon={<FaChartLine />}>
              <NavLink exact to={paths.dashboard}>
                {trans({ id: 'dashboard', defaultMessage: 'Dashboard' })}
              </NavLink>
            </MenuItem>
          </Tooltip>
          <Tooltip title={trans({ id: 'notifications_page_title', defaultMessage: 'Notifications' })} placement="right" disableHoverListener={!navigationExpanded}>
            <MenuItem active={location.pathname === paths.notifications} icon={<FaBell />}>
              <NavLink exact to={paths.notifications}>
                {trans({ id: 'notifications_page_title', defaultMessage: 'Notifications' })}
              </NavLink>
            </MenuItem>
          </Tooltip>
          {canAccessPlanModule('pos') && sidebarTokenAllowed('pos', access || { ready: false }) && (
            <Tooltip title={trans({ id: 'pos', defaultMessage: 'POS' })} placement="right" disableHoverListener={!navigationExpanded}>
              <MenuItem active={location.pathname === paths.pos} icon={<DesktopMacOutlinedIcon fontSize="small" />}>
                <NavLink exact to={paths.pos}>
                  {trans({ id: 'pos', defaultMessage: 'POS' })}
                </NavLink>
              </MenuItem>
            </Tooltip>
          )}
          {isSuperAdmin && (
            <>
              <Tooltip title={trans({ id: 'dashboard_sa_sales_leads_title', defaultMessage: 'Requests' })} placement="right" disableHoverListener={!navigationExpanded}>
                <MenuItem active={location.pathname.startsWith(paths.admin.salesLeads.list)} icon={<FaEnvelopeOpenText />}>
                  <NavLink exact to={paths.admin.salesLeads.list}>
                    {trans({ id: 'dashboard_sa_sales_leads_title', defaultMessage: 'Requests' })}
                  </NavLink>
                </MenuItem>
              </Tooltip>
              <Tooltip title={trans({ id: 'recent_activity', defaultMessage: 'Recent Activity' })} placement="right" disableHoverListener={!navigationExpanded}>
                <MenuItem active={location.pathname === paths.admin.activity} icon={<FaHistory />}>
                  <NavLink exact to={paths.admin.activity}>
                    {trans({ id: 'recent_activity', defaultMessage: 'Recent Activity' })}
                  </NavLink>
                </MenuItem>
              </Tooltip>
              <Tooltip title={trans({ id: 'platform_health', defaultMessage: 'Platform Health' })} placement="right" disableHoverListener={!navigationExpanded}>
                <MenuItem active={location.pathname === paths.admin.platformHealth} icon={<FaHeartbeat />}>
                  <NavLink exact to={paths.admin.platformHealth}>
                    {trans({ id: 'platform_health', defaultMessage: 'Platform Health' })}
                  </NavLink>
                </MenuItem>
              </Tooltip>
              <Tooltip title={trans({ id: 'admin_social_prompt_title', defaultMessage: 'Social post prompt' })} placement="right" disableHoverListener={!navigationExpanded}>
                <MenuItem active={location.pathname === paths.admin.socialContentPrompt} icon={<FaPen />}>
                  <NavLink exact to={paths.admin.socialContentPrompt}>
                    {trans({ id: 'admin_social_prompt_title', defaultMessage: 'Social post prompt' })}
                  </NavLink>
                </MenuItem>
              </Tooltip>
              <Tooltip title={trans({ id: 'admin_vision_prompt_title', defaultMessage: 'Vision prompt' })} placement="right" disableHoverListener={!navigationExpanded}>
                <MenuItem active={location.pathname === paths.admin.creativeVisionPrompt} icon={<FaImage />}>
                  <NavLink exact to={paths.admin.creativeVisionPrompt}>
                    {trans({ id: 'admin_vision_prompt_title', defaultMessage: 'Vision prompt' })}
                  </NavLink>
                </MenuItem>
              </Tooltip>
              <Tooltip title={trans({ id: 'admin_push_page_title', defaultMessage: 'Push notifications' })} placement="right" disableHoverListener={!navigationExpanded}>
                <MenuItem active={location.pathname.startsWith(paths.admin.pushNotifications)} icon={<FaBell />}>
                  <NavLink exact to={paths.admin.pushNotificationsSend}>
                    {trans({ id: 'admin_push_page_title', defaultMessage: 'Push notifications' })}
                  </NavLink>
                </MenuItem>
              </Tooltip>
              {/* <Tooltip title={trans({ id: "users", defaultMessage: "Users" })} placement="right" disableHoverListener={!navigationExpanded}>
								<MenuItem active={location.pathname === "/user_managment"} icon={<FaListOl />}>
									<NavLink exact to={"/user_managment"}>{trans({ id: "users", defaultMessage: "Users" })}</NavLink>
								</MenuItem>
							</Tooltip> */}
            </>
          )}

          {isSuperAdmin && (
            <SidebarItem title={trans({ id: 'packages', defaultMessage: 'Packages' })} icon={<FiPackage />} routeConfig={pathnames.packages}>
              <MenuItem active={location.pathname === paths.packages.list} icon={<FaListOl />}>
                <NavLink exact to={paths.packages.list}>
                  {trans({ id: 'packages', defaultMessage: 'Packages' })}
                </NavLink>
              </MenuItem>
              <MenuItem active={location.pathname === paths.packages.new} icon={<FaPlus />}>
                <NavLink exact to={paths.packages.new}>
                  {trans({ id: 'create_package', defaultMessage: 'Create Package' })}
                </NavLink>
              </MenuItem>
            </SidebarItem>
          )}

          {isSuperAdmin && (
            <SidebarItem title={trans({ id: 'company_management', defaultMessage: 'Company Management' })} icon={<FaStackOverflow />} routeConfig={pathnames.companies}>
              <MenuItem active={location.pathname === paths.admin.companies || location.pathname.startsWith(`${paths.admin.companies}/`)} icon={<FaListOl />}>
                <NavLink exact to={paths.admin.companies}>
                  {trans({ id: 'companies', defaultMessage: 'Companies' })}
                </NavLink>
              </MenuItem>
              <MenuItem active={location.pathname === paths.companies.deleted} icon={<FaListOl />}>
                <NavLink exact to={paths.companies.deleted}>
                  {trans({ id: 'deleted_companies', defaultMessage: 'Deleted Companies' })}
                </NavLink>
              </MenuItem>
              <MenuItem active={location.pathname === paths.admin.accountDeletions} icon={<FaListOl />}>
                <NavLink exact to={paths.admin.accountDeletions}>
                  {trans({ id: 'dashboard_sa_account_deletions_title', defaultMessage: 'Pending deletions' })}
                </NavLink>
              </MenuItem>
              <MenuItem active={location.pathname === paths.admin.superAdmins || /\/admin\/super-admins\/[^/]+\/edit$/.test(location.pathname)} icon={<FaUserShield />}>
                <NavLink exact to={paths.admin.superAdmins}>
                  {trans({ id: 'dashboard_sa_platform_admins_title', defaultMessage: 'Super Admins' })}
                </NavLink>
              </MenuItem>
              <MenuItem active={location.pathname === paths.admin.superAdminNew} icon={<FaPlus />}>
                <NavLink exact to={paths.admin.superAdminNew}>
                  {trans({ id: 'dashboard_sa_create_super_admin', defaultMessage: 'Create Super Admin' })}
                </NavLink>
              </MenuItem>
              <MenuItem active={location.pathname === paths.companies.new} icon={<FaPlus />}>
                <NavLink exact to={paths.companies.new}>
                  {trans({ id: 'create_company', defaultMessage: 'Create Company' })}
                </NavLink>
              </MenuItem>
            </SidebarItem>
          )}

          {canShowUserManagementNav(userInfo) && (
            <SidebarItem title={trans({ id: 'user_management', defaultMessage: 'User Management' })} icon={<FaUsersCog style={{ fontSize: '1.05rem' }} />} routeConfig={pathnames.user_management}>
              <MenuItem active={location.pathname === paths.users.list} icon={<FaListOl />}>
                <NavLink exact to={paths.users.list}>
                  {trans({ id: 'users', defaultMessage: 'Users' })}
                </NavLink>
              </MenuItem>
              <MenuItem active={location.pathname === paths.users.new} icon={<FaPlus />}>
                <NavLink exact to={paths.users.new}>
                  {trans({ id: 'create_user', defaultMessage: 'Create User' })}
                </NavLink>
              </MenuItem>
            </SidebarItem>
          )}

          {allowedModulesArray.includes('Products') && (
            <SidebarItem title={trans({ id: 'products', defaultMessage: 'Products' })} icon={<FaBoxOpen style={{ fontSize: '1.05rem' }} />} routesActivate={pathnames.products}>
              <MenuItem active={location.pathname === paths.products.list} icon={<FaListOl />}>
                <NavLink exact to={paths.products.list}>
                  {trans({ id: 'products', defaultMessage: 'Products' })}
                </NavLink>
              </MenuItem>
              {allowPermission('catalog.create') ? (
                <MenuItem active={location.pathname === paths.products.new} icon={<FaPlus />}>
                  <NavLink exact to={paths.products.new}>
                    {trans({ id: 'create_product', defaultMessage: 'Create Product' })}
                  </NavLink>
                </MenuItem>
              ) : null}
              {allowPermission('catalog.view') ? (
                <MenuItem active={location.pathname === paths.labors.list} icon={<FaUsersCog style={{ fontSize: '1.05rem' }} />}>
                  <NavLink exact to={paths.labors.list}>
                    {trans({ id: 'labors', defaultMessage: 'Labors' })}
                  </NavLink>
                </MenuItem>
              ) : null}
              {allowPermission('catalog.create') ? (
                <MenuItem active={location.pathname === paths.labors.new} icon={<FaPlus />}>
                  <NavLink exact to={paths.labors.new}>
                    {trans({ id: 'create_labor', defaultMessage: 'Create Labor' })}
                  </NavLink>
                </MenuItem>
              ) : null}
              <MenuItem active={location.pathname === paths.brands.list} icon={<FaTags style={{ fontSize: '1.05rem' }} />}>
                <NavLink exact to={paths.brands.list}>
                  {trans({ id: 'brands', defaultMessage: 'Brands' })}
                </NavLink>
              </MenuItem>
              <MenuItem active={location.pathname === paths.productTags.list} icon={<FaTag style={{ fontSize: '1.05rem' }} />}>
                <NavLink exact to={paths.productTags.list}>
                  {trans({ id: 'product_tags', defaultMessage: 'Product tags' })}
                </NavLink>
              </MenuItem>
              <MenuItem active={location.pathname === paths.productBadges.list} icon={<FaCertificate style={{ fontSize: '1.05rem' }} />}>
                <NavLink exact to={paths.productBadges.list}>
                  {trans({ id: 'product_badges', defaultMessage: 'Product badges' })}
                </NavLink>
              </MenuItem>
              <MenuItem active={location.pathname === paths.attributeDefinitions.list} icon={<FaListOl style={{ fontSize: '1.05rem' }} />}>
                <NavLink exact to={paths.attributeDefinitions.list}>
                  {trans({ id: 'attributes', defaultMessage: 'Attributes' })}
                </NavLink>
              </MenuItem>
              <MenuItem active={location.pathname === paths.categories.list} icon={<FaShapes style={{ fontSize: '1.05rem' }} />}>
                <NavLink exact to={paths.categories.list}>
                  {trans({ id: 'categories', defaultMessage: 'Categories' })}
                </NavLink>
              </MenuItem>
              <MenuItem active={location.pathname === paths.locations.list} icon={<FaMapMarkedAlt style={{ fontSize: '1.05rem' }} />}>
                <NavLink exact to={paths.locations.list}>
                  {trans({ id: 'storage_locations', defaultMessage: 'Storage Locations' })}
                </NavLink>
              </MenuItem>
              <MenuItem active={location.pathname === paths.uoms.list} icon={<FaCogs style={{ fontSize: '1.05rem' }} />}>
                <NavLink exact to={paths.uoms.list}>
                  {trans({ id: 'uom', defaultMessage: 'UOM' })}
                </NavLink>
              </MenuItem>
              {!profileLocked ? (
                <MenuItem active={location.pathname === paths.taxes.list} icon={<FaFileInvoiceDollar style={{ fontSize: '1.05rem' }} />}>
                  <NavLink exact to={paths.taxes.list}>
                    {trans({ id: 'taxes', defaultMessage: 'Taxes' })}
                  </NavLink>
                </MenuItem>
              ) : null}
              <MenuItem active={location.pathname === paths.payTerms.list} icon={<FaCalendarCheck style={{ fontSize: '1.05rem' }} />}>
                <NavLink exact to={paths.payTerms.list}>
                  {trans({ id: 'pay_terms', defaultMessage: 'Pay Terms' })}
                </NavLink>
              </MenuItem>
              <MenuItem active={location.pathname === paths.workOrderTypes.list} icon={<FaTools style={{ fontSize: '1.05rem' }} />}>
                <NavLink exact to={paths.workOrderTypes.list}>
                  {trans({ id: 'work_order_types', defaultMessage: 'Work Order Types' })}
                </NavLink>
              </MenuItem>
              <MenuItem active={location.pathname === '/barcode-generator'} icon={<FaBarcode style={{ fontSize: '1.05rem' }} />}>
                <NavLink exact to={paths.barcodeGenerator}>
                  {trans({ id: 'barcode_generator', defaultMessage: 'Barcode Generator' })}
                </NavLink>
              </MenuItem>
            </SidebarItem>
          )}

          {allowedModulesArray.includes('WareHouse') && (
            <SidebarItem title={trans({ id: 'warehouses', defaultMessage: 'Warehouses' })} icon={<FaWarehouse />} routeConfig={pathnames.warehouses}>
              <MenuItem active={location.pathname === paths.warehouses.list} icon={<FaListOl />}>
                <NavLink exact to={paths.warehouses.list}>
                  {trans({ id: 'warehouses', defaultMessage: 'Warehouses' })}
                </NavLink>
              </MenuItem>
              {!profileLocked || companyAdmin ? (
                <MenuItem active={location.pathname === paths.warehouses.new} icon={<FaPlus />}>
                  <NavLink exact to={paths.warehouses.new}>
                    {trans({ id: 'create_warehouse', defaultMessage: 'Create Warehouse' })}
                  </NavLink>
                </MenuItem>
              ) : null}
            </SidebarItem>
          )}

          {allowedModulesArray.includes('Vendor') && (
            <SidebarItem title={trans({ id: 'vendor', defaultMessage: 'Vendor' })} icon={<FaUserTag style={{ fontSize: '1.1rem' }} />} routeConfig={pathnames.vendors}>
              <MenuItem active={location.pathname === paths.vendors.list} icon={<FaListOl />}>
                <NavLink exact to={paths.vendors.list}>
                  {trans({ id: 'vendors', defaultMessage: 'Vendors' })}
                </NavLink>
              </MenuItem>
              <MenuItem active={location.pathname === paths.vendors.new} icon={<FaPlus />}>
                <NavLink exact to={paths.vendors.new}>
                  {trans({ id: 'create_vendor', defaultMessage: 'Create Vendor' })}
                </NavLink>
              </MenuItem>
            </SidebarItem>
          )}

          {allowedModulesArray.includes('Stock Receipt') && (
            <SidebarItem title={trans({ id: 'stock_receipt', defaultMessage: 'Stock Receipt' })} icon={<FaBoxes />} routeConfig={pathnames.stock_receipts}>
              {/* <MenuItem active={location.pathname === "/stock_receipt"} icon={<FaListOl />}>
								<NavLink exact to={"/stock_receipt"}>{trans({ id: "stock_receipt", defaultMessage: "Stock Receipt" })}</NavLink>
							</MenuItem>
							<MenuItem active={location.pathname === "/add_stock_receipt"} icon={<FaPlus />}>
								<NavLink exact to={"/add_stock_receipt"}>{trans({ id: "create_stock_receipt", defaultMessage: "Create Stock Receipt" })}</NavLink>
							</MenuItem> */}
              <MenuItem active={location.pathname === paths.stockReceipts.list} icon={<FaListOl />}>
                <NavLink exact to={paths.stockReceipts.list}>
                  {trans({ id: 'stock_receipt', defaultMessage: 'Stock Receipt' })}
                </NavLink>
              </MenuItem>
              <MenuItem active={location.pathname === paths.stockReceipts.new} icon={<FaPlus />}>
                <NavLink exact to={paths.stockReceipts.new}>
                  {trans({ id: 'create_stock_receipt', defaultMessage: 'Create Stock Receipt' })}
                </NavLink>
              </MenuItem>
            </SidebarItem>
          )}

          {(allowedModulesArray.includes('Stock Transfer') || allowedModulesArray.includes('Stock Receipt')) && (
            <SidebarItem title={trans({ id: 'stock_transfer', defaultMessage: 'Stock Transfer' })} icon={<FaExchangeAlt />} routeConfig={pathnames.stock_transfers}>
              <MenuItem active={location.pathname === paths.stockTransfers.list} icon={<FaListOl />}>
                <NavLink exact to={paths.stockTransfers.list}>
                  {trans({ id: 'transfers', defaultMessage: 'Transfers' })}
                </NavLink>
              </MenuItem>
              <MenuItem active={location.pathname === paths.stockTransfers.new} icon={<FaPlus />}>
                <NavLink exact to={paths.stockTransfers.new}>
                  {trans({ id: 'new_transfer', defaultMessage: 'New Transfer' })}
                </NavLink>
              </MenuItem>
            </SidebarItem>
          )}

          {allowedModulesArray.includes('Purchase Order') && (
            <SidebarItem title={trans({ id: 'purchase_order', defaultMessage: 'Purchase Order' })} icon={<FaClipboardList />} routeConfig={pathnames.purchase_orders}>
              <MenuItem active={location.pathname === paths.purchaseOrders.list} icon={<FaListOl />}>
                <NavLink exact to={paths.purchaseOrders.list}>
                  {trans({ id: 'purchase_orders', defaultMessage: 'Purchase Orders' })}
                </NavLink>
              </MenuItem>
              <MenuItem active={location.pathname === paths.purchaseOrders.new} icon={<FaPlus />}>
                <NavLink exact to={paths.purchaseOrders.new}>
                  {trans({ id: 'po_new', defaultMessage: 'New Purchase Order' })}
                </NavLink>
              </MenuItem>
            </SidebarItem>
          )}

          {(planModules.includes('Expensive') || planModules.includes('Purchase Order')) && allowPermission('vendor_bill.view') && (
            <SidebarItem title={trans({ id: 'vb_menu_title', defaultMessage: 'Vendor Bills' })} icon={<FaMoneyBillWave />} routeConfig={pathnames.vendor_bills}>
              <MenuItem active={location.pathname === paths.vendorBills.list} icon={<FaListOl />}>
                <NavLink exact to={paths.vendorBills.list}>
                  {trans({ id: 'vb_page_title', defaultMessage: 'Vendor Bills' })}
                </NavLink>
              </MenuItem>
              <MenuItem active={location.pathname === paths.vendorBills.new} icon={<FaPlus />}>
                <NavLink exact to={paths.vendorBills.new}>
                  {trans({ id: 'vb_create_bill', defaultMessage: 'Create Bill' })}
                </NavLink>
              </MenuItem>
            </SidebarItem>
          )}

          {allowedModulesArray.includes('Adjustment') && (
            <SidebarItem title={trans({ id: 'adjustment', defaultMessage: 'Adjustment' })} icon={<FaSlidersH />} routeConfig={pathnames.inventory_adjustments}>
              <MenuItem active={location.pathname === paths.adjustments.list} icon={<FaListOl />}>
                <NavLink exact to={paths.adjustments.list}>
                  {trans({ id: 'adjustments', defaultMessage: 'Adjustments' })}
                </NavLink>
              </MenuItem>
              <MenuItem active={location.pathname === paths.adjustments.new} icon={<FaPlus />}>
                <NavLink exact to={paths.adjustments.new}>
                  {trans({ id: 'create_adjustment', defaultMessage: 'Create Adjustment' })}
                </NavLink>
              </MenuItem>
            </SidebarItem>
          )}

          {allowedModulesArray.includes('Costumers') && (
            <SidebarItem title={trans({ id: 'customers', defaultMessage: 'Customers' })} icon={<FaUsers style={{ fontSize: '1.05rem' }} />} routeConfig={pathnames.customers}>
              <MenuItem active={location.pathname === paths.customers.list} icon={<FaListOl />}>
                <NavLink exact to={paths.customers.list}>
                  {trans({ id: 'customers', defaultMessage: 'Customers' })}
                </NavLink>
              </MenuItem>
              <MenuItem active={location.pathname === paths.customers.new} icon={<FaPlus />}>
                <NavLink exact to={paths.customers.new}>
                  {trans({ id: 'create_customer', defaultMessage: 'Create Customer' })}
                </NavLink>
              </MenuItem>
            </SidebarItem>
          )}

          {allowedModulesArray.includes('Sales') && (
            <SidebarItem title={trans({ id: 'sales', defaultMessage: 'Sales' })} icon={<FaDollarSign style={{ fontSize: '1.05rem' }} />} routesActivate={pathnames.sales}>
              <MenuItem active={location.pathname === paths.invoices.list} icon={<FaListOl />}>
                <NavLink exact to={paths.invoices.list}>
                  {trans({ id: 'sales', defaultMessage: 'Sales' })}
                </NavLink>
              </MenuItem>
              {allowPermission('invoice.view') ? (
                <MenuItem active={location.pathname === paths.onlineSales.list} icon={<FaListOl />}>
                  <NavLink exact to={paths.onlineSales.list}>
                    {trans({ id: 'online', defaultMessage: 'Online' })}
                  </NavLink>
                </MenuItem>
              ) : null}
              {isCompanyAdmin(userInfo) || (profileLocked && hasPermission(access, 'return.view')) ? (
                <MenuItem active={location.pathname.startsWith(paths.saleReturns.list)} icon={<FaUndo />}>
                  <NavLink exact to={paths.saleReturns.list}>
                    {trans({ id: 'sale_returns', defaultMessage: 'Returns' })}
                  </NavLink>
                </MenuItem>
              ) : null}
              {allowPermission('invoice.view') ? (
                <MenuItem active={location.pathname.startsWith(paths.storefrontInquiries.list)} icon={<FaEnvelopeOpenText />}>
                  <NavLink exact to={paths.storefrontInquiries.list}>
                    {trans({ id: 'inquiries_page_title', defaultMessage: 'Inquiries' })}
                  </NavLink>
                </MenuItem>
              ) : null}
              {allowPermission('invoice.view') ? (
                <MenuItem active={location.pathname.startsWith('/recurring-invoices')} icon={<FaListOl />}>
                  <NavLink exact to={paths.recurringInvoices.list}>
                    {trans({ id: 'recurring_invoices', defaultMessage: 'Recurring Invoices' })}
                  </NavLink>
                </MenuItem>
              ) : null}
              {allowPermission('work_order.view') ? (
                <MenuItem active={location.pathname === paths.workOrders.list} icon={<FaListOl />}>
                  <NavLink exact to={paths.workOrders.list}>
                    {trans({ id: 'work_order', defaultMessage: 'Work Order' })}
                  </NavLink>
                </MenuItem>
              ) : null}
              {allowPermission('payment.view') ? (
                <MenuItem active={location.pathname === paths.receivePayments.list} icon={<FaMoneyBillWave />}>
                  <NavLink exact to={paths.receivePayments.list}>
                    {trans({ id: 'receive_payment', defaultMessage: 'Receive Payment' })}
                  </NavLink>
                </MenuItem>
              ) : null}
              {allowPermission('work_order.view') ? (
                <MenuItem active={location.pathname.startsWith('/devices')} icon={<FaLayerGroup />}>
                  <NavLink exact to={paths.devices.list}>
                    {trans({ id: 'devices', defaultMessage: 'Devices' })}
                  </NavLink>
                </MenuItem>
              ) : null}
              {allowPermission('payment.view') ? (
                <MenuItem active={location.pathname === paths.workOrderPayments.list} icon={<FaLayerGroup />}>
                  <NavLink exact to={paths.workOrderPayments.list}>
                    {trans({ id: 'work_order_payments', defaultMessage: 'Work Order Payments' })}
                  </NavLink>
                </MenuItem>
              ) : null}
              {allowPermission('work_order.view') ? (
                <MenuItem active={location.pathname === paths.deviceTypes.list} icon={<FaLayerGroup />}>
                  <NavLink exact to={paths.deviceTypes.list}>
                    {trans({ id: 'device_types', defaultMessage: 'Device types' })}
                  </NavLink>
                </MenuItem>
              ) : null}
            </SidebarItem>
          )}

          {allowedModulesArray.includes('Expensive') && (allowPermission('expense.view') || allowPermission('payment.view')) && (
            <SidebarItem title={trans({ id: 'expenses', defaultMessage: 'Expenses' })} icon={<FaDollarSign />} routeConfig={pathnames.expenses}>
              {allowPermission('expense.view') ? (
                <MenuItem active={location.pathname === paths.expenses.list} icon={<FaListOl />}>
                  <NavLink exact to={paths.expenses.list}>
                    {trans({ id: 'expenses', defaultMessage: 'Expenses' })}
                  </NavLink>
                </MenuItem>
              ) : null}
              {allowPermission('expense.draft') ? (
                <MenuItem active={location.pathname === paths.expenses.new} icon={<FaPlus />}>
                  <NavLink exact to={paths.expenses.new}>
                    {trans({ id: 'create_expense', defaultMessage: 'Create Expense' })}
                  </NavLink>
                </MenuItem>
              ) : null}
              {allowPermission('expense.view') ? (
                <MenuItem active={location.pathname === paths.expenseCategories.list || location.pathname.startsWith(`${paths.expenseCategories.list}/`)} icon={<FaListOl />}>
                  <NavLink exact to={paths.expenseCategories.list}>
                    {trans({ id: 'expense_categories', defaultMessage: 'Expense Categories' })}
                  </NavLink>
                </MenuItem>
              ) : null}
              {allowPermission('payment.view') ? (
                <MenuItem active={location.pathname === paths.paymentAccounts.list} icon={<FaListAlt />}>
                  <NavLink exact to={paths.paymentAccounts.list}>
                    {trans({ id: 'payment_account', defaultMessage: 'Payment Account' })}
                  </NavLink>
                </MenuItem>
              ) : null}
              {allowPermission('payment.view') ? (
                <MenuItem active={location.pathname === paths.paymentTypes.list} icon={<FaCcMastercard />}>
                  <NavLink exact to={paths.paymentTypes.list}>
                    {trans({ id: 'payment_types', defaultMessage: 'Payment Types' })}
                  </NavLink>
                </MenuItem>
              ) : null}
            </SidebarItem>
          )}

          {canAccessPlanModule('reports') && sidebarTokenAllowed('Reports', access || { ready: false }) && (
            <SidebarItem title={trans({ id: 'accounting', defaultMessage: 'Accounting' })} icon={<FaCalculator style={{ fontSize: '1.05rem' }} />} routesActivate={pathnames.reports}>
              <MenuItem active={location.pathname === paths.accounting.overview} icon={<FaListOl />}>
                <NavLink exact to={paths.accounting.overview}>
                  {trans({ id: 'accounting_overview', defaultMessage: 'Overview' })}
                </NavLink>
              </MenuItem>
              <MenuItem active={location.pathname === paths.accounting.profitLoss} icon={<FaListOl />}>
                <NavLink exact to={paths.accounting.profitLoss}>
                  {trans({ id: 'profit_loss', defaultMessage: 'Profit & Loss' })}
                </NavLink>
              </MenuItem>
              <MenuItem active={location.pathname === paths.accounting.accountsReceivable} icon={<FaListOl />}>
                <NavLink exact to={paths.accounting.accountsReceivable}>
                  {trans({ id: 'accounts_receivable', defaultMessage: 'Accounts Receivable' })}
                </NavLink>
              </MenuItem>
              <MenuItem active={location.pathname === paths.accounting.salesReport} icon={<FaListOl />}>
                <NavLink exact to={paths.accounting.salesReport}>
                  {trans({ id: 'sales_report', defaultMessage: 'Sales Report' })}
                </NavLink>
              </MenuItem>
              <MenuItem active={location.pathname === paths.accounting.paymentsReport} icon={<FaListOl />}>
                <NavLink exact to={paths.accounting.paymentsReport}>
                  {trans({ id: 'payments_report', defaultMessage: 'Payments Report' })}
                </NavLink>
              </MenuItem>
              <MenuItem active={location.pathname === paths.accounting.expensesReport} icon={<FaListOl />}>
                <NavLink exact to={paths.accounting.expensesReport}>
                  {trans({ id: 'expenses_report', defaultMessage: 'Expenses Report' })}
                </NavLink>
              </MenuItem>
            </SidebarItem>
          )}

          {(allowPermission('catalog.view') || allowPermission('catalog.update')) && (
            <SidebarItem title={trans({ id: 'marketing', defaultMessage: 'Marketing' })} icon={<FaBullhorn />} routesActivate={pathnames.marketing}>
              <MenuItem active={location.pathname === paths.marketing.socialContent} icon={<FaBullhorn />}>
                <NavLink exact to={paths.marketing.socialContent}>
                  {trans({ id: 'social_media_ai', defaultMessage: 'Social Media AI' })}
                </NavLink>
              </MenuItem>
              <MenuItem active={location.pathname === paths.marketing.creativeStudio} icon={<FaImage />}>
                <NavLink exact to={paths.marketing.creativeStudio}>
                  {trans({ id: 'creative_studio', defaultMessage: 'Creative Studio' })}
                </NavLink>
              </MenuItem>
            </SidebarItem>
          )}

          {(allowedModulesArray.includes('Configuration') || canAccessPlanModule('settings')) && (
            <SidebarItem title={trans({ id: 'settings', defaultMessage: 'Settings' })} icon={<FaCog />} routesActivate={settingsNavRoutes}>
              {allowPermission('report.view') ? (
                <MenuItem active={location.pathname === paths.settings.chartOfAccounts} icon={<FaChartPie />}>
                  <NavLink exact to={paths.settings.chartOfAccounts}>
                    {trans({ id: 'chart_of_accounts', defaultMessage: 'Chart of Accounts' })}
                  </NavLink>
                </MenuItem>
              ) : null}
              {companyAdmin ? (
                <MenuItem active={location.pathname === SettingsRoutes[0].path} icon={<FaRegCreditCard />}>
                  <NavLink exact to={SettingsRoutes[0].path}>
                    {trans({ id: 'payment', defaultMessage: 'Payment' })}
                  </NavLink>
                </MenuItem>
              ) : null}
              {companyAdmin ? (
                <MenuItem active={location.pathname === SettingsRoutes[1].path} icon={<FaSms />}>
                  <NavLink exact to={SettingsRoutes[1].path}>
                    {trans({ id: 'sms', defaultMessage: 'SMS' })}
                  </NavLink>
                </MenuItem>
              ) : null}
              {companyAdmin ? (
                <MenuItem active={location.pathname === SettingsRoutes[2].path} icon={<FaBell />}>
                  <NavLink exact to={SettingsRoutes[2].path}>
                    {trans({ id: 'push_notifications', defaultMessage: 'Push Notifications' })}
                  </NavLink>
                </MenuItem>
              ) : null}
              {showShopLegalNav(userInfo) ? (
                <MenuItem active={location.pathname === paths.settings.storefrontProfile} icon={<FaListOl />}>
                  <NavLink exact to={paths.settings.storefrontProfile}>
                    {trans({ id: 'storefront_profile', defaultMessage: 'Storefront profile' })}
                  </NavLink>
                </MenuItem>
              ) : null}
              {showShopLegalNav(userInfo) ? (
                <MenuItem active={location.pathname === paths.settings.legalPolicies} icon={<FaListOl />}>
                  <NavLink exact to={paths.settings.legalPolicies}>
                    {trans({ id: 'storefront_legal_title', defaultMessage: 'Legal & Policies' })}
                  </NavLink>
                </MenuItem>
              ) : null}
              {companyAdmin ? (
                <MenuItem active={location.pathname === paths.settings.subscription} icon={<FaLayerGroup />}>
                  <NavLink exact to={paths.settings.subscription}>
                    {trans({ id: 'subscription_usage', defaultMessage: 'Subscription & usage' })}
                  </NavLink>
                </MenuItem>
              ) : null}
              {!profileLocked || companyAdmin ? (
                <MenuItem active={location.pathname === paths.settings.account} icon={<FaUserShield />}>
                  <NavLink exact to={paths.settings.account}>
                    {trans({ id: 'account', defaultMessage: 'Account' })}
                  </NavLink>
                </MenuItem>
              ) : null}
            </SidebarItem>
          )}
        </Menu>
      </SidebarContent>
      <SidebarFooter style={{ borderTop: '1px solid rgba(255,255,255,0.15)', padding: '12px 15px', background: '#062B5B' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#10B981',
            fontSize: '10px',
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '10px',
            paddingLeft: !navigationExpanded ? '4px' : '12px',
            paddingBlock: '1rem',
          }}
        >
          <span style={{ display: 'inline-block', width: '8px', height: '8px', backgroundColor: '#10B981', borderRadius: '50%', boxShadow: '0 0 8px #10B981', flexShrink: 0 }}></span>
          {!navigationExpanded && (process.env.NODE_ENV === 'production' ? 'Production' : 'Development')}
        </div>
        <Tooltip title={`${userInfo.name || 'User'} (${userInfo.roles?.[0]?.name || 'Admin'})`} placement="right" disableHoverListener={!navigationExpanded}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', overflow: 'hidden' }}>
            <img style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '2px solid rgba(255,255,255,0.2)' }} src={AvatarDefault} alt="Avatar" />
            {!navigationExpanded && (
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#ffffff', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', textTransform: 'capitalize' }}>{userInfo.name || 'User'}</span>
                <span style={{ fontSize: '10px', color: '#b0c2de', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', textTransform: 'capitalize' }}>{userInfo.roles?.[0]?.name || 'Admin'}</span>
              </div>
            )}
          </div>
        </Tooltip>
      </SidebarFooter>
    </ProSidebar>
  )
}

export default Aside
